// src/components/circuit/editors.jsx
// Editor condivisi (settori S1/S2/S3, area box, traguardo) + Modal.
// Estratti da Circuits.jsx per riuso fra "modifica circuito" ed editor manuale.
// Le classi CSS `.circ-*` provengono da Circuits.css (importato dalle pagine che li usano);
// i controlli usano i primitivi globali `.rs-*` di tokens.css.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Polygon, CircleMarker, useMapEvents, useMap } from 'react-leaflet';
import {
    IconLayers, IconPlus, IconTrash, IconChevronDown, IconBox, IconMapPin,
    IconEdit, IconSave, IconUndo, IconFlag, IconX,
} from './icons';
import { nearestTrackIndex, startLineLatLngs } from './geometry';

/* ===== Modal ===== */
export function Modal({ open, onClose, children }) {
    if (!open) return null;
    return (
        <div className="rs-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="rs-modal rs-modal--lg circ-modal" onClick={(e) => e.stopPropagation()}>
                <button className="rs-btn rs-btn--ghost rs-btn--icon circ-modal__close" onClick={onClose} aria-label="Chiudi">
                    <IconX size={18} />
                </button>
                {children}
            </div>
        </div>
    );
}

/* ===== Editor Settori S1/S2/S3 ===== */
export function SectorEditor({ totalPoints, sectors, onChange }) {
    const [editingSector, setEditingSector] = useState(null);

    const addSector = () => {
        if (sectors.length >= 3) return;
        const newSector = {
            id: Date.now(),
            name: `Settore ${sectors.length + 1}`,
            startIdx: 0,
            endIdx: Math.floor(totalPoints / 3),
            color: ['#e10600', '#21c130', '#0066ff'][sectors.length],
        };
        onChange([...sectors, newSector]);
        setEditingSector(newSector.id);
    };

    const updateSector = (id, updates) => {
        onChange(sectors.map(s => (s.id === id ? { ...s, ...updates } : s)));
    };

    const removeSector = (id) => {
        onChange(sectors.filter(s => s.id !== id));
        if (editingSector === id) setEditingSector(null);
    };

    return (
        <div className="circ-ed">
            <div className="circ-ed__head">
                <span className="circ-ed__title">
                    <IconLayers size={14} />
                    <span>Settori</span>
                    <span className="rs-badge">{sectors.length}/3</span>
                </span>
                {sectors.length < 3 && (
                    <button className="rs-btn rs-btn--sm rs-btn--outline" onClick={addSector}>
                        <IconPlus size={14} />
                        <span>Aggiungi</span>
                    </button>
                )}
            </div>

            <div className="circ-ed__list">
                {sectors.map(sector => (
                    <div key={sector.id} className={`circ-ed__item ${editingSector === sector.id ? 'is-open' : ''}`}>
                        <div
                            className="circ-ed__item-head"
                            onClick={() => setEditingSector(editingSector === sector.id ? null : sector.id)}
                        >
                            <span className="rs-color-dot" style={{ background: sector.color }} />
                            <span className="circ-ed__item-text">
                                <strong>{sector.name}</strong>
                                <small>Punti {sector.startIdx} – {sector.endIdx}</small>
                            </span>
                            <span className="circ-ed__item-actions">
                                <button
                                    className="rs-btn rs-btn--ghost rs-btn--icon rs-btn--sm circ-ed__del"
                                    onClick={(e) => { e.stopPropagation(); removeSector(sector.id); }}
                                    title="Elimina settore"
                                    type="button"
                                >
                                    <IconTrash size={14} />
                                </button>
                                <IconChevronDown size={16} className={`rs-chevron ${editingSector === sector.id ? 'rs-chevron--open' : ''}`} />
                            </span>
                        </div>

                        {editingSector === sector.id && (
                            <div className="circ-ed__item-body">
                                <div className="rs-field">
                                    <label className="rs-label">Nome settore</label>
                                    <input
                                        className="rs-input"
                                        value={sector.name}
                                        onChange={(e) => updateSector(sector.id, { name: e.target.value })}
                                        placeholder="Es. Settore 1"
                                    />
                                </div>
                                <div className="rs-grid rs-grid--2 circ-ed__row">
                                    <div className="rs-field">
                                        <label className="rs-label">Punto iniziale</label>
                                        <input
                                            className="rs-input rs-input--mono"
                                            type="number"
                                            min={0}
                                            max={totalPoints - 1}
                                            value={sector.startIdx}
                                            onChange={(e) => updateSector(sector.id, { startIdx: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>
                                    <div className="rs-field">
                                        <label className="rs-label">Punto finale</label>
                                        <input
                                            className="rs-input rs-input--mono"
                                            type="number"
                                            min={0}
                                            max={totalPoints - 1}
                                            value={sector.endIdx}
                                            onChange={(e) => updateSector(sector.id, { endIdx: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>
                                </div>
                                <div className="rs-field">
                                    <label className="rs-label">Colore settore</label>
                                    <div className="circ-color">
                                        <input
                                            type="color"
                                            value={sector.color}
                                            onChange={(e) => updateSector(sector.id, { color: e.target.value })}
                                        />
                                        <span className="circ-color__value">{sector.color}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {sectors.length === 0 && (
                <div className="rs-empty circ-ed__empty">
                    <IconLayers size={24} />
                    <strong>Nessun settore definito</strong>
                    <p>Aggiungi fino a 3 settori per dividere il circuito in sezioni.</p>
                </div>
            )}
        </div>
    );
}

/* ===== Mappa: handler click & fit ===== */
function BoxMapClickHandler({ onMapClick }) {
    useMapEvents({
        click(e) { onMapClick({ lat: e.latlng.lat, lon: e.latlng.lng }); },
    });
    return null;
}

export function BoxMapFitter({ trackPoints }) {
    const map = useMap();
    const fittedRef = useRef(false);
    useEffect(() => {
        if (fittedRef.current) return;
        if (!trackPoints || trackPoints.length < 2) return;
        const lats = trackPoints.map(p => p.lat);
        const lons = trackPoints.map(p => p.lon);
        const bounds = [
            [Math.min(...lats), Math.min(...lons)],
            [Math.max(...lats), Math.max(...lons)],
        ];
        map.fitBounds(bounds, { padding: [30, 30] });
        fittedRef.current = true;
    }, [map, trackPoints]);
    return null;
}

/* ===== Editor Area Box (Pitbox) ===== */
export function BoxEditor({ trackPoints, pitboxArea, onChange }) {
    const [drawing, setDrawing] = useState(false);
    const [currentPoints, setCurrentPoints] = useState(pitboxArea || []);

    useEffect(() => { setCurrentPoints(pitboxArea || []); }, [pitboxArea]);

    const center = useMemo(() => {
        if (!trackPoints || trackPoints.length === 0) return [45.0, 9.0];
        const lat = trackPoints.reduce((s, p) => s + p.lat, 0) / trackPoints.length;
        const lon = trackPoints.reduce((s, p) => s + p.lon, 0) / trackPoints.length;
        return [lat, lon];
    }, [trackPoints]);

    const handleMapClick = useCallback((point) => {
        if (!drawing) return;
        const updated = [...currentPoints, point];
        setCurrentPoints(updated);
        onChange(updated);
    }, [drawing, currentPoints, onChange]);

    const handleUndo = () => {
        if (currentPoints.length === 0) return;
        const updated = currentPoints.slice(0, -1);
        setCurrentPoints(updated);
        onChange(updated);
    };

    const handleClear = () => {
        setCurrentPoints([]);
        onChange([]);
        setDrawing(false);
    };

    const trackLatLngs = useMemo(() => {
        if (!trackPoints || trackPoints.length < 2) return [];
        return trackPoints.map(p => [p.lat, p.lon]);
    }, [trackPoints]);

    const boxLatLngs = useMemo(() => currentPoints.map(p => [p.lat, p.lon]), [currentPoints]);

    return (
        <div className="circ-ed">
            <div className="circ-ed__head">
                <span className="circ-ed__title">
                    <IconBox size={14} />
                    <span>Area box</span>
                    {currentPoints.length > 0 && <span className="rs-badge rs-badge--warning">{currentPoints.length} punti</span>}
                </span>
            </div>

            <div className="rs-alert circ-ed__hint">
                <IconMapPin size={14} />
                <span>
                    {drawing
                        ? 'Clicca sulla mappa per aggiungere punti al poligono dei box'
                        : currentPoints.length > 0
                            ? 'Area box definita. Premi "Modifica" per ridisegnare.'
                            : 'Premi "Disegna box" per iniziare a tracciare l\'area dei box sulla mappa'}
                </span>
            </div>

            <div className={`circ-map ${drawing ? 'is-drawing' : ''}`}>
                <MapContainer center={center} zoom={17} style={{ height: '100%', width: '100%' }} zoomControl={true}>
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution="Esri"
                        maxNativeZoom={19}
                        maxZoom={22}
                    />
                    <BoxMapFitter trackPoints={trackPoints} />
                    {trackLatLngs.length > 1 && (
                        <Polyline positions={trackLatLngs} pathOptions={{ color: '#22c55e', weight: 3, opacity: 0.8 }} />
                    )}
                    {boxLatLngs.length > 0 && (
                        <Polygon
                            positions={boxLatLngs}
                            pathOptions={{
                                color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.25, weight: 2,
                                dashArray: currentPoints.length < 3 ? '6, 6' : undefined,
                            }}
                        />
                    )}
                    {drawing && <BoxMapClickHandler onMapClick={handleMapClick} />}
                </MapContainer>
                {drawing && <span className="rs-badge rs-badge--solid-red circ-map__badge"><span className="rs-badge__dot rs-badge__dot--live" />Disegno attivo</span>}
            </div>

            <div className="circ-ed__actions">
                {!drawing ? (
                    <button className="rs-btn rs-btn--outline-red" onClick={() => setDrawing(true)} type="button">
                        <IconEdit size={14} />
                        <span>{currentPoints.length > 0 ? 'Modifica' : 'Disegna box'}</span>
                    </button>
                ) : (
                    <button className="rs-btn rs-btn--lime" onClick={() => setDrawing(false)} type="button">
                        <IconSave size={14} />
                        <span>Fine disegno</span>
                    </button>
                )}
                {currentPoints.length > 0 && (
                    <>
                        <button className="rs-btn rs-btn--outline rs-btn--icon" onClick={handleUndo} title="Annulla ultimo punto" type="button">
                            <IconUndo size={14} />
                        </button>
                        <button className="rs-btn rs-btn--danger rs-btn--icon" onClick={handleClear} title="Cancella tutto" type="button">
                            <IconTrash size={14} />
                        </button>
                    </>
                )}
            </div>

            {currentPoints.length === 0 && !drawing && (
                <div className="rs-empty circ-ed__empty">
                    <IconBox size={24} />
                    <strong>Nessuna area box definita</strong>
                    <p>Disegna un poligono sulla mappa per definire l'area dei box.</p>
                </div>
            )}
        </div>
    );
}

/* ===== Editor Traguardo ===== */
function FinishMapClickHandler({ onSelect }) {
    useMapEvents({
        click(e) { onSelect({ lat: e.latlng.lat, lon: e.latlng.lng }); },
    });
    return null;
}

export function FinishEditor({ trackPoints, selectedIndex, widthMeters, onChange }) {
    const center = useMemo(() => {
        if (!trackPoints || trackPoints.length === 0) return [45.0, 9.0];
        const lat = trackPoints.reduce((s, p) => s + p.lat, 0) / trackPoints.length;
        const lon = trackPoints.reduce((s, p) => s + p.lon, 0) / trackPoints.length;
        return [lat, lon];
    }, [trackPoints]);

    const trackLatLngs = useMemo(() => (
        Array.isArray(trackPoints) ? trackPoints.map(p => [p.lat, p.lon]) : []
    ), [trackPoints]);

    const selectedPoint = trackPoints?.[selectedIndex] || trackPoints?.[0] || null;
    const lineLatLngs = useMemo(() => startLineLatLngs(trackPoints, selectedIndex, widthMeters), [trackPoints, selectedIndex, widthMeters]);

    const handleMapSelect = useCallback((point) => {
        onChange(nearestTrackIndex(trackPoints, point));
    }, [onChange, trackPoints]);

    return (
        <div className="circ-ed">
            <div className="circ-ed__head">
                <span className="circ-ed__title">
                    <IconFlag size={14} />
                    <span>Traguardo / linea del via</span>
                    <span className="rs-badge rs-badge--red">Punto {selectedIndex}</span>
                </span>
            </div>

            <div className="rs-alert circ-ed__hint">
                <IconMapPin size={14} />
                <span>Clicca sulla mappa satellitare: il sistema aggancia il traguardo al punto più vicino del tracciato.</span>
            </div>

            <div className="circ-map">
                <MapContainer center={center} zoom={18} style={{ height: '100%', width: '100%' }} zoomControl={true}>
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution="Esri"
                        maxNativeZoom={19}
                        maxZoom={22}
                    />
                    <BoxMapFitter trackPoints={trackPoints} />
                    {trackLatLngs.length > 1 && (
                        <Polyline positions={trackLatLngs} pathOptions={{ color: '#22c55e', weight: 4, opacity: 0.85 }} />
                    )}
                    {lineLatLngs.length === 2 && (
                        <Polyline positions={lineLatLngs} pathOptions={{ color: '#ffffff', weight: 5, opacity: 1 }} />
                    )}
                    {selectedPoint && (
                        <CircleMarker
                            center={[selectedPoint.lat, selectedPoint.lon]}
                            radius={8}
                            pathOptions={{ color: '#ffffff', fillColor: '#e10600', fillOpacity: 1, weight: 3 }}
                        />
                    )}
                    <FinishMapClickHandler onSelect={handleMapSelect} />
                </MapContainer>
                <span className="rs-badge circ-map__badge">Clicca per spostare il traguardo</span>
            </div>

            <div className="circ-ed__actions">
                <button className="rs-btn rs-btn--outline rs-btn--icon" type="button" onClick={() => onChange(0)} title="Ripristina traguardo originale">
                    <IconUndo size={14} />
                </button>
                <p className="rs-help circ-ed__note">
                    Il salvataggio ruota il tracciato: gare e allenamenti useranno questo punto come start/finish.
                </p>
            </div>
        </div>
    );
}
